import React, { useMemo, useRef, useState } from "react";
import { useLocation, useNavigate, Link } from "react-router-dom";
import { Button } from "../components/ui/button";
import { Textarea } from "../components/ui/textarea";
import { Card } from "../components/ui/card";
import { Tabs, TabsList, TabsTrigger, TabsContent } from "../components/ui/tabs";
import { ArrowLeft, FileText, ScanSearch, ShieldCheck, Sparkles, Upload, WandSparkles } from "lucide-react";
import Header from "../components/Header";
import { useAuth } from "../lib/AuthContext";
import { extractTextFromFile } from "../lib/file-text-extractor";

const API_BASE = process.env.REACT_APP_API_BASE_URL || "http://localhost:4000";
const PLAGIARISM_API_BASE = process.env.REACT_APP_PLAGIARISM_API_URL || "http://localhost:4001";

const sanitizeSentenceAnalysisForHistory = (items = []) =>
  Array.isArray(items)
    ? items.map((item = {}) => ({
        sentence: typeof item.sentence === "string" ? item.sentence : "",
        confidence: Number(item.confidence ?? 0),
        label: typeof item.label === "string" ? item.label : undefined,
        is_suspicious: typeof item.is_suspicious === "boolean" ? item.is_suspicious : undefined,
        is_plagiarized: typeof item.is_plagiarized === "boolean" ? item.is_plagiarized : undefined,
        source_title: typeof item.source_title === "string" ? item.source_title : undefined,
        source_url: typeof item.source_url === "string" ? item.source_url : undefined,
      }))
    : [];

const sanitizeTopMatchesForHistory = (items = []) =>
  Array.isArray(items)
    ? items.map((item = {}) => ({
        doc_id: typeof item.doc_id === "string" ? item.doc_id : undefined,
        title: typeof item.title === "string" ? item.title : undefined,
        url: typeof item.url === "string" ? item.url : undefined,
        score: Number(item.score ?? 0),
        num_chunks: Number(item.num_chunks ?? 0),
      }))
    : [];

const sanitizeBestMatchForHistory = (item) => {
  if (!item || typeof item !== "object") {
    return null;
  }

  return {
    doc_id: typeof item.doc_id === "string" ? item.doc_id : undefined,
    title: typeof item.title === "string" ? item.title : undefined,
    url: typeof item.url === "string" ? item.url : undefined,
    final_score: Number(item.final_score ?? 0),
    heuristic_label: Number(item.heuristic_label ?? 0),
  };
};

const sanitizeHistoryResult = (result = {}) => {
  if (result?.type === "ai_check" || typeof result?.combined_prob_ai === "number") {
    return {
      type: "ai_check",
      combined_prob_ai: Number(result.combined_prob_ai ?? 0),
      combined_label: result.combined_label || "Nguoi viet",
      combined_threshold: Number(result.combined_threshold ?? 0.5),
      sentence_analysis: sanitizeSentenceAnalysisForHistory(result.sentence_analysis),
      stats: result.stats ?? {},
      wordCount: Number(result.wordCount ?? 0),
    };
  }

  return {
    label: Number(result.label ?? 0),
    heuristic_label: Number(result.heuristic_label ?? 0),
    result_text: result.result_text ?? "",
    plagiarism_type: result.plagiarism_type ?? null,
    is_plagiarism: !!result.is_plagiarism,
    confidence: Number(result.confidence ?? 0),
    threshold: Number(result.threshold ?? 0),
    best_match: sanitizeBestMatchForHistory(result.best_match),
    top_matches: sanitizeTopMatchesForHistory(result.top_matches),
    sentence_analysis: sanitizeSentenceAnalysisForHistory(result.sentence_analysis),
    stats: result.stats ?? {},
  };
};

const SAMPLE_TEXTS = {
  plagiarism: [
    {
      title: "Đoạn văn học thuật",
      text: "Biến đổi khí hậu đang tạo ra những tác động sâu rộng lên hệ sinh thái toàn cầu. Nhiệt độ trung bình tăng, quy luật mưa thay đổi và các hiện tượng thời tiết cực đoan xuất hiện dày đặc hơn, từ đó làm suy giảm đa dạng sinh học ở nhiều khu vực.",
    },
    {
      title: "Báo cáo nghiên cứu ngắn",
      text: "Việc ứng dụng phân tích dữ liệu trong quản trị đại học giúp nhà trường cải thiện chất lượng ra quyết định, tối ưu phân bổ nguồn lực và nâng cao trải nghiệm học tập của sinh viên thông qua các chỉ số theo dõi cụ thể.",
    },
  ],
  ai: [
    {
      title: "Đoạn nghi ngờ AI",
      text: "Trí tuệ nhân tạo đang nhanh chóng thay đổi cách con người tạo ra nội dung. Các mô hình ngôn ngữ có thể sản xuất văn bản mạch lạc, rõ ràng và giàu thông tin chỉ trong vài giây, từ đó tạo ra thách thức mới cho việc đánh giá tính nguyên bản trong môi trường học thuật.",
    },
    {
      title: "Đoạn mô tả trung tính",
      text: "Bài viết này trình bày khái quát về nhu cầu xây dựng công cụ hỗ trợ phát hiện nội dung do AI tạo ra, đồng thời đề xuất một quy trình kiểm tra kết hợp giữa phân tích mô hình và đánh giá theo từng câu trong văn bản.",
    },
  ],
};

const MODE_CONFIG = {
  plagiarism: {
    title: "Kiểm tra đạo văn",
    subtitle:
      "Đối chiếu mức độ tương đồng theo từng đoạn và hỗ trợ rà soát sơ bộ trước phản hồi học thuật hoặc lưu hồ sơ nội bộ.",
    badge: "Plagiarism workflow",
    accentClass: "checker-accent-plagiarism",
    heroCardClass: "checker-hero-card-plagiarism",
    panelClass: "checker-panel-plagiarism",
    uploadHint: "Phù hợp cho bài tiểu luận, báo cáo học phần, mô tả đề tài hoặc văn bản học thuật cần rà soát tính nguyên bản.",
    primaryLabel: "Kiểm tra đạo văn ngay",
    tabIntro: "Chọn cách nhập liệu phù hợp cho quy trình kiểm tra đạo văn.",
    samples: SAMPLE_TEXTS.plagiarism,
    icon: ScanSearch,
  },
  ai: {
    title: "Kiểm tra dấu hiệu AI",
    subtitle:
      "Đánh dấu những đoạn có tín hiệu giống văn bản tạo sinh, giúp giảng viên hoặc đơn vị chuyên môn dễ xem xét và giải thích kết quả.",
    badge: "AI detector workflow",
    accentClass: "checker-accent-ai",
    heroCardClass: "checker-hero-card-ai",
    panelClass: "checker-panel-ai",
    uploadHint: "Thích hợp cho văn bản cần rà soát dấu hiệu AI trước khi phản hồi, chấm điểm hoặc kiểm tra nội bộ trong cơ sở đào tạo.",
    primaryLabel: "Phân tích dấu hiệu AI",
    tabIntro: "Chọn cách nhập liệu phù hợp cho quy trình phát hiện dấu hiệu AI.",
    samples: SAMPLE_TEXTS.ai,
    icon: WandSparkles,
  },
};

const CheckerPage = () => {
  const location = useLocation();
  const navigate = useNavigate();
  const fileInputRef = useRef(null);
  const [text, setText] = useState("");
  const [isChecking, setIsChecking] = useState(false);
  const [isExtractingFile, setIsExtractingFile] = useState(false);
  const [fileExtractionMessage, setFileExtractionMessage] = useState("");
  const [wordCount, setWordCount] = useState(0);
  const [selectedFile, setSelectedFile] = useState(null);
  const [isDragging, setIsDragging] = useState(false);
  const { token } = useAuth();

  const mode = location.pathname === "/ai" ? "ai" : "plagiarism";
  const config = useMemo(() => MODE_CONFIG[mode], [mode]);
  const ModeIcon = config.icon;

  const getFileExtension = (fileName = "") => {
    const parts = fileName.toLowerCase().split(".");
    return parts.length > 1 ? parts.pop() : "";
  };

  const handleTextChange = (e) => {
    const newText = e.target.value;
    setText(newText);
    const words = newText
      .trim()
      .split(/\s+/)
      .filter((word) => word.length > 0);
    setWordCount(words.length);
  };

  const updateTextState = (nextText) => {
    setText(nextText);
    const words = nextText
      .trim()
      .split(/\s+/)
      .filter((word) => word.length > 0);
    setWordCount(words.length);
  };

  const processSelectedFile = async (file) => {
    if (!file) {
      return;
    }

    setSelectedFile(file);
    setFileExtractionMessage("");

    const validExtensions = [".txt", ".pdf", ".doc", ".docx"];
    const lowerName = file.name.toLowerCase();
    const isValidFile = validExtensions.some((extension) => lowerName.endsWith(extension));

    if (!isValidFile) {
      alert("Vui lòng tải lên file TXT, PDF văn bản, DOCX hoặc DOC.");
      setSelectedFile(null);
      return;
    }

    try {
      setIsExtractingFile(true);
      const extension = getFileExtension(file.name);
      setFileExtractionMessage(
        extension === "doc"
          ? `Đang trích xuất nội dung từ file .doc: ${file.name}...`
          : `Đang đọc nội dung từ file ${file.name}...`
      );
      const extractedText = await extractTextFromFile(file, {
        backendBaseUrl: API_BASE,
      });

      if (!extractedText.trim()) {
        throw new Error("Không trích xuất được nội dung văn bản từ file này.");
      }

      updateTextState(extractedText);
      const extractedWords = extractedText
        .trim()
        .split(/\s+/)
        .filter((word) => word.length > 0).length;
      setFileExtractionMessage(
        extension === "doc"
          ? `Đã trích xuất ${extractedWords} từ từ file .doc ${file.name}.`
          : `Đã đọc ${extractedWords} từ từ file ${file.name}.`
      );
    } catch (error) {
      console.error("Error extracting text from file:", error);
      setSelectedFile(null);
      updateTextState("");
      setFileExtractionMessage("");
      alert(error.message || "Không thể đọc nội dung từ file đã chọn.");
    } finally {
      setIsExtractingFile(false);
    }
  };

  const handleFileChange = async (e) => {
    const file = e.target.files?.[0];
    await processSelectedFile(file);

    if (e.target) {
      e.target.value = "";
    }
  };

  const handleDragOver = (e) => {
    e.preventDefault();
    e.stopPropagation();
    setIsDragging(true);
  };

  const handleDragLeave = (e) => {
    e.preventDefault();
    e.stopPropagation();
    setIsDragging(false);
  };

  const handleDrop = async (e) => {
    e.preventDefault();
    e.stopPropagation();
    setIsDragging(false);

    const file = e.dataTransfer.files?.[0];
    await processSelectedFile(file);
  };

  const handleSampleClick = (sampleText) => {
    setText(sampleText);
    const words = sampleText
      .trim()
      .split(/\s+/)
      .filter((word) => word.length > 0);
    setWordCount(words.length);
  };

  const saveHistoryReport = async (rawResult) => {
    if (!token) {
      return;
    }

    const response = await fetch(`${API_BASE}/api/history`, {
      method: "POST",
      headers: {
        "Content-Type": "application/json",
        Authorization: `Bearer ${token}`,
      },
      body: JSON.stringify({
        text,
        result: sanitizeHistoryResult(rawResult),
        inputType: "text",
      }),
    });

    if (!response.ok) {
      let message = `Failed to save history/report: ${response.status}`;

      try {
        const data = await response.json();
        message = data?.message || message;
      } catch {
        // Ignore non-JSON error bodies.
      }

      throw new Error(message);
    }
  };

  const handleCheckPlagiarism = async () => {
    if (!text.trim()) {
      alert("Vui lòng nhập nội dung để kiểm tra đạo văn.");
      return;
    }

    setIsChecking(true);

    try {
      const response = await fetch(`${PLAGIARISM_API_BASE}/api/check-plagiarism`, {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
        },
        body: JSON.stringify({ text }),
      });

      if (!response.ok) {
        throw new Error(`API error: ${response.status}`);
      }

      const result = await response.json();

      try {
        await saveHistoryReport(result);
      } catch (e) {
        console.warn("Failed to save history/report:", e);
      }

      navigate("/results", { state: { result } });
    } catch (error) {
      console.error("Error checking plagiarism:", error);
      alert("Không thể kiểm tra đạo văn. Hãy chắc chắn plagiarism API đang chạy ở cổng 4001.");
    } finally {
      setIsChecking(false);
    }
  };

  const handleCheckAI = async () => {
    if (!text.trim()) {
      alert("Vui lòng nhập nội dung để kiểm tra dấu hiệu AI.");
      return;
    }

    setIsChecking(true);

    try {
      const response = await fetch(`${API_BASE}/api/ai-check`, {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
        },
        body: JSON.stringify({ text }),
      });

      if (!response.ok) {
        throw new Error(`API error: ${response.status}`);
      }

      const result = await response.json();
      const aiPayload = result?.data && typeof result.data === "object" ? result.data : result;

      const aiResult = {
        type: "ai_check",
        combined_prob_ai: aiPayload?.combined_prob_ai ?? 0,
        combined_label: aiPayload?.combined_label || "Nguoi viet",
        combined_threshold: aiPayload?.combined_threshold ?? 0.5,
        sentence_analysis: aiPayload?.sentence_analysis || [],
        high_risk_sentences: aiPayload?.high_risk_sentences || [],
        stats: aiPayload?.stats || {},
        analyzedText: text,
        wordCount: text.trim().split(/\s+/).length,
      };

      try {
        await saveHistoryReport(aiResult);
      } catch (e) {
        console.warn("Failed to save history/report:", e);
      }

      navigate("/results", { state: { result: aiResult } });
    } catch (error) {
      console.error("Error checking AI content:", error);
      alert("Không thể kiểm tra AI. Hãy chắc chắn node-auth và AI detector API đang hoạt động.");
    } finally {
      setIsChecking(false);
    }
  };

  const primaryAction = mode === "ai" ? handleCheckAI : handleCheckPlagiarism;

  return (
    <div className="app-shell">
      <Header />

      <main className="page-main">
        <div className="max-w-6xl mx-auto px-4 py-8">
          <div className="mb-6">
            <Link to="/">
              <Button variant="ghost" size="sm">
                <ArrowLeft className="w-4 h-4 mr-2" />
                Về trang tổng quan
              </Button>
            </Link>
          </div>

          <section className={`checker-intro-band ${config.heroCardClass}`}>
            <div className="checker-intro-copy">
              <div className={`checker-mode-badge ${config.accentClass}`}>
                <ModeIcon className="h-4 w-4" />
                {config.badge}
              </div>
              <h1 className="checker-page-title">{config.title}</h1>
              <p className="checker-page-subtitle">{config.subtitle}</p>
            </div>

            <div className="checker-intro-pills">
              <div className="checker-intro-pill">
                <ShieldCheck className="h-4 w-4" />
                <span>{mode === "ai" ? "Phân tích theo từng câu" : "Đối chiếu theo từng đoạn"}</span>
              </div>
              <div className="checker-intro-pill">
                <FileText className="h-4 w-4" />
                <span>Hỗ trợ TXT, PDF, DOCX, DOC</span>
              </div>
            </div>
          </section>

          <Card className="checker-workspace-card">
            <div className="checker-workspace-head">
              <div>
                <div className="checker-workspace-kicker">Nhập nội dung</div>
                <h2 className="checker-workspace-title">{config.tabIntro}</h2>
              </div>
              <div className={`checker-workspace-dot ${config.accentClass}`} />
            </div>

            <Tabs defaultValue="text">
              <TabsList className="grid w-full grid-cols-3 mb-6 checker-tabs-list">
                <TabsTrigger triggerValue="text">
                  <FileText className="w-4 h-4 mr-2" />
                  Văn bản
                </TabsTrigger>
                <TabsTrigger triggerValue="file">
                  <Upload className="w-4 h-4 mr-2" />
                  Tải file
                </TabsTrigger>
                <TabsTrigger triggerValue="samples">
                  <Sparkles className="w-4 h-4 mr-2" />
                  Mẫu thử
                </TabsTrigger>
              </TabsList>

              <TabsContent contentValue="text">
                <Textarea
                  placeholder={mode === "ai" ? "Dán nội dung cần kiểm tra dấu hiệu AI..." : "Dán nội dung cần kiểm tra đạo văn..."}
                  value={text}
                  onChange={handleTextChange}
                  className="min-h-[360px] mb-4 checker-textarea"
                />
                <div className="checker-action-row">
                  <span className="text-sm text-muted-foreground">Số từ hiện tại: {wordCount}</span>
                  <Button
                    onClick={primaryAction}
                    disabled={isChecking || isExtractingFile || !text.trim()}
                    size="lg"
                    className={`checker-submit-button ${config.accentClass}`}
                  >
                    {isChecking ? "Đang xử lý..." : config.primaryLabel}
                  </Button>
                </div>
              </TabsContent>

              <TabsContent contentValue="file">
                <div
                  className={`checker-upload-dropzone ${isDragging ? "checker-upload-dropzone-active" : ""}`}
                  onDragOver={handleDragOver}
                  onDragLeave={handleDragLeave}
                  onDrop={handleDrop}
                >
                  <Upload className="w-12 h-12 mx-auto mb-4 text-muted-foreground" />
                  <p className="mb-3 checker-upload-title">
                    {isDragging ? "Thả file vào đây" : "Kéo thả file hoặc bấm để chọn"}
                  </p>
                  <p className="mb-4 text-sm text-muted-foreground">
                    Hỗ trợ PDF có thể chọn được văn bản, DOCX và DOC. Chưa hỗ trợ PDF scan dạng ảnh.
                  </p>
                  <input
                    ref={fileInputRef}
                    type="file"
                    onChange={handleFileChange}
                    accept=".txt,.pdf,.doc,.docx"
                    id="file-upload-input"
                    style={{ display: "none" }}
                  />
                  <Button
                    variant="outline"
                    type="button"
                    disabled={isExtractingFile}
                    onClick={(e) => {
                      e.preventDefault();
                      const input = document.getElementById("file-upload-input");
                      if (input) {
                        input.click();
                      }
                    }}
                  >
                    {isExtractingFile ? "Đang đọc file..." : "Chọn file"}
                  </Button>
                  {selectedFile && <p className="mt-4 text-sm text-muted-foreground">Đã chọn: {selectedFile.name}</p>}
                  {fileExtractionMessage && (
                    <p className={`mt-2 text-sm ${isExtractingFile ? "text-blue-600" : "text-green-700"}`}>
                      {fileExtractionMessage}
                    </p>
                  )}
                </div>

                {text && (
                  <>
                    <Textarea value={text} onChange={handleTextChange} className="min-h-[280px] mb-4 checker-textarea" />
                    <div className="checker-action-row">
                      <span className="text-sm text-muted-foreground">Số từ hiện tại: {wordCount}</span>
                      <Button
                        onClick={primaryAction}
                        disabled={isChecking || isExtractingFile || !text.trim()}
                        size="lg"
                        className={`checker-submit-button ${config.accentClass}`}
                      >
                        {isChecking ? "Đang xử lý..." : config.primaryLabel}
                      </Button>
                    </div>
                  </>
                )}
              </TabsContent>

              <TabsContent contentValue="samples">
                <div className="space-y-4 mb-4">
                  {config.samples.map((sample) => (
                    <Card
                      key={sample.title}
                      className="p-4 cursor-pointer hover:bg-accent transition-colors checker-sample-card"
                      onClick={() => handleSampleClick(sample.text)}
                    >
                      <h3 className="font-semibold mb-2">{sample.title}</h3>
                      <p className="text-sm text-muted-foreground line-clamp-3">{sample.text}</p>
                    </Card>
                  ))}
                </div>

                {text && (
                  <>
                    <Textarea value={text} onChange={handleTextChange} className="min-h-[280px] mb-4 checker-textarea" />
                    <div className="checker-action-row">
                      <span className="text-sm text-muted-foreground">Số từ hiện tại: {wordCount}</span>
                      <Button
                        onClick={primaryAction}
                        disabled={isChecking || isExtractingFile || !text.trim()}
                        size="lg"
                        className={`checker-submit-button ${config.accentClass}`}
                      >
                        {isChecking ? "Đang xử lý..." : config.primaryLabel}
                      </Button>
                    </div>
                  </>
                )}
              </TabsContent>
            </Tabs>
          </Card>

          <Card className={`checker-context-card ${config.panelClass}`}>
            <div className="checker-side-label">Gợi ý sử dụng</div>
            <p className="checker-side-text">{config.uploadHint}</p>

            <div className="checker-side-points">
              <div className="checker-side-point">
                <ShieldCheck className="h-4 w-4" />
                <span>{mode === "ai" ? "Phù hợp để rà soát dấu hiệu AI trước phản hồi hoặc chấm điểm." : "Phù hợp để rà soát tính nguyên bản trước nghiệm thu hoặc phản hồi học thuật."}</span>
              </div>
              <div className="checker-side-point">
                <FileText className="h-4 w-4" />
                <span>Tập trung vào phần nhập liệu và kết quả trực quan, phần giới thiệu chỉ đóng vai trò hỗ trợ.</span>
              </div>
            </div>
          </Card>
        </div>
      </main>
    </div>
  );
};

export default CheckerPage;
